import React, { useCallback, useEffect, useMemo } from 'react';
import { createContext, ReactNode, useState } from 'react';
import { useGetPostalInformation } from '../../api/Postal';
import { useTrackUpdatePostal } from '../../hooks/tracking/useTrackingEvents';
import { useMember } from '../member';

const LOCAL_STORAGE_POSTAL_KEY = 'USER_POSTAL';

const initialPostal = localStorage.getItem(LOCAL_STORAGE_POSTAL_KEY);

export const getPostal = () => localStorage.getItem(LOCAL_STORAGE_POSTAL_KEY);

export const PostalContext = createContext<IPostalContext | undefined>(undefined);

interface IPostalProvider {
  children: ReactNode;
}

const PostalProvider = ({ children }: IPostalProvider) => {
  const [postal, setPostal] = useState(initialPostal);
  const [isPostal, setIsPostal] = useState(false);
  const { member } = useMember();

  const [internalPostal, setInternalPostal] = useState(initialPostal);
  const [memberPostal] = member?.member?.postal.split(' ') || [];

  const {
    data: postalInformation,
    isSuccess: postalSuccess,
    isError: postalError,
    isLoading: loadingPostalInformation,
  } = useGetPostalInformation(internalPostal);

  const trackUpdatePostal = useTrackUpdatePostal();

  useEffect(() => {
    if (memberPostal && internalPostal !== memberPostal) {
      setInternalPostal(memberPostal);
      setPostal(memberPostal);
    }
  }, [memberPostal]);

  useEffect(() => {
    if (postalSuccess && internalPostal) {
      setPostal(internalPostal);
      localStorage.setItem(LOCAL_STORAGE_POSTAL_KEY, internalPostal);
      setInternalPostal(null);
    }
  }, [internalPostal, postalSuccess, postalInformation]);

  const reset = useCallback(() => {
    setPostal(null);
    setInternalPostal(null);
    localStorage.removeItem(LOCAL_STORAGE_POSTAL_KEY);
  }, []);

  useEffect(() => {
    if (postal && postal !== initialPostal) {
      trackUpdatePostal(postal);
    }
  }, [initialPostal, postal]);

  const value = useMemo(
    () => ({
      error: postalError,
      loading: loadingPostalInformation || (!!internalPostal && !postal),

      postal,
      setPostal: setInternalPostal,
      postalInformation,

      reset,
      isPostal,
      setIsPostal,
    }),
    [
      postalError,
      loadingPostalInformation,

      setPostal,
      postalSuccess,
      postal,
      postalInformation,

      reset,
      isPostal,
      setIsPostal,
    ],
  );

  return <PostalContext.Provider value={value}>{children}</PostalContext.Provider>;
};

export default PostalProvider;
